import { getToolStep } from "@src/features/ai/utils/thought-process-steps";

import { FlattenedPart } from "@src/features/ai/components/WorkflowEditor/execution/types";

/**
 * A run of consecutive parts that render as the identical thought-process step.
 * Only the first part is rendered; `repeatCount` is how many times the agent
 * made that same call in a row (1 for a one-off), so the row can read
 * "Listing Products ×3" instead of stacking three identical rows.
 */
export type CollapsedStep = {
  part: FlattenedPart;
  repeatCount: number;
};

/**
 * Two tool calls collapse only when they render the same row: same tool, same
 * step title, same description. Keying off the rendered copy (rather than the
 * raw arguments) is what makes two "Listing Products" calls that differ only in
 * arguments the row never shows collapse, while two calls that describe
 * different work stay two rows.
 *
 * Parts that aren't tool steps — reasoning, text, and the clarifying-question
 * tool that renders as assistant text — get no signature and never collapse.
 */
function stepSignature(item: FlattenedPart): string | undefined {
  const { part } = item;

  if (part.type !== "dynamic-tool" || part.toolName === "request-user-input") {
    return undefined;
  }

  const { title, description } = getToolStep(part.toolName, part.input);

  return JSON.stringify([part.toolName, title, description ?? ""]);
}

/**
 * Collapse consecutive identical tool steps into one entry carrying a repeat
 * count. Non-adjacent repeats stay separate — the timeline is chronological, so
 * merging across an intervening step would misreport the order of the work.
 */
export function collapseRepeatedSteps(parts: FlattenedPart[]): CollapsedStep[] {
  return parts.reduce<CollapsedStep[]>((collapsed, item) => {
    const previous = collapsed[collapsed.length - 1];
    const signature = stepSignature(item);

    if (
      previous &&
      signature !== undefined &&
      signature === stepSignature(previous.part)
    ) {
      previous.repeatCount += 1;
      return collapsed;
    }

    collapsed.push({ part: item, repeatCount: 1 });
    return collapsed;
  }, []);
}
